<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12">
                <div class="d-flex align-center flex-wrap gap-2">
                  <v-btn-toggle
                    v-model="selectedStatusFilter"
                    mandatory
                    color="primary"
                    rounded="pill"
                    density="compact"
                    class="flex-wrap"
                  >
                    <v-btn
                      v-for="option in statusFilterOptions"
                      :key="option.value"
                      :value="option.value"
                      :color="selectedStatusFilter === option.value ? option.color : ''"
                      variant="tonal"                   
                      rounded="pill"
                    >
                      <v-icon size="small" start>{{ option.icon }}</v-icon>
                      {{ option.title }}
                      <v-badge
                        v-if="option.value !== 'All'"
                        :content="getStatusCount(option.value)"
                        :color="option.color"
                        inline
                        class="ml-2"
                      />
                      <span v-else class="ml-2">({{ totalTaskCount }})</span>
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-tabs v-model="activeTab" color="primary" class="mb-4">
            <v-tab value="timeline">
              <v-icon start>mdi-timeline</v-icon>
              Timeline
            </v-tab>
            <v-tab value="calendar">
              <v-icon start>mdi-calendar</v-icon>
              Calendar
            </v-tab>            
          </v-tabs>
          
          <v-card-text>
            <v-tabs-window v-model="activeTab">
              <!-- Calendar Tab -->
              <v-tabs-window-item value="calendar">
                <ejs-schedule 
                  height="650px"
                  width="100%"
                  :selectedDate="selectedDate"
                  :eventSettings="eventSettings"
                  :currentView="currentView"
                  :showQuickInfo="true"
                  :showWeekend="true"
                  :cssClass="themeMode ? 'e-dark' : 'e-light'"
                  @eventClick="handleEventClick"
                  @eventRendered="onEventRendered"
                >
                  <e-views>
                    <e-view option="Day"></e-view>
                    <e-view option="Week"></e-view>
                    <e-view option="WorkWeek"></e-view>
                    <e-view option="Month"></e-view>
                    <e-view option="Year"></e-view>
                    <e-view option="Agenda"></e-view>
                  </e-views>
                </ejs-schedule>
              </v-tabs-window-item>
              
              <!-- Timeline Tab -->
              <v-tabs-window-item value="timeline">
                <v-toolbar class="mb-4" color="background">
                  <v-btn
                    icon
                    variant="text"
                    @click="navigateDate('prev')"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  
                  <v-btn
                    variant="text"
                    @click="navigateToToday"
                  >
                    Today
                  </v-btn>
                  
                  <v-btn
                    icon
                    variant="text"
                    @click="navigateDate('next')"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                  
                  <v-toolbar-title class="mx-4">
                    {{ getCurrentDateRange() }}
                  </v-toolbar-title>
                  
                  <v-spacer></v-spacer>
                  
                  <v-btn-toggle
                    v-model="timelineView"
                    mandatory
                    color="primary"
                    rounded="pill"
                  >
                    <v-btn
                      v-for="option in timelineViewOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      <v-icon size="small" start>{{ option.icon }}</v-icon>
                      {{ option.displayName }}
                    </v-btn>
                  </v-btn-toggle>
                </v-toolbar>
                <ejs-schedule 
                  ref="timelineScheduler"
                  height="650px"
                  width="100%"
                  :selectedDate="selectedDate"
                  :eventSettings="timelineEventSettings"
                  :views="['TimelineDay', 'TimelineWeek', 'TimelineWorkWeek', 'TimelineMonth', 'TimelineYear', 'Agenda']"
                  :currentView="timelineView"
                  :workDays="workDays"
                  :group="group"
                  :showQuickInfo="true"
                  :showWeekend="true"
                  :showHeaderBar="false"
                  :cssClass="themeMode ? 'e-dark' : 'e-light'"
                  @eventClick="handleEventClick"
                  @eventRendered="onEventRendered"
                >
                  <e-resources>
                    <e-resource 
                      field="TaskId" 
                      title="Parent Tasks" 
                      name="Tasks" 
                      :dataSource="taskResourceDataSource" 
                      :allowMultiple="allowMultiple"
                      textField="text" 
                      idField="id" 
                      colorField="color">
                    </e-resource>
                  </e-resources>    
                </ejs-schedule>
              </v-tabs-window-item>
            </v-tabs-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Task Dialog -->
    <TaskDialog 
      v-model="taskDialogOpen" 
      :task="selectedTask"
    />
  </v-container>
</template>

<script setup>
import { ref, provide, onMounted, computed, watch, nextTick } from 'vue';
import { useTheme } from 'vuetify';

const theme = useTheme();

const themeMode = computed(() => {
  return theme.global.name.value === 'dark' ? true : false;
 });

import { 
  ScheduleComponent as EjsSchedule,
  ViewsDirective as EViews,
  ViewDirective as EView,
  ResourceDirective as EResource,
  ResourcesDirective as EResources,
  Day, Week, WorkWeek, Month, Year, Agenda, TimelineMonth, TimelineYear, TimelineViews, DragAndDrop, Resize 
} from '@syncfusion/ej2-vue-schedule';
import { useStore } from 'vuex';
import TaskDialog from './dialogs/TaskDialog.vue';
import { format, addDays, addWeeks, addMonths, addYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

// Make sure the resource components are available globally
const components = {
  'e-resource': EResource,
  'e-resources': EResources
};

const store = useStore();
const conn = store.getters.getDBConnection;
const duckDBService = store.getters.getDuckDBService;
const settings = computed(() => store.state.settings);

// Provide schedule services
provide('schedule', [Day, Week, WorkWeek, Month, Year, Agenda, TimelineMonth, TimelineYear, TimelineViews, DragAndDrop, Resize]);

// Reactive data
const selectedDate = ref(new Date());
const currentView = ref('Month');
const timelineView = ref('TimelineMonth');
const timelineViewOptions = ref([
  { value: 'TimelineDay', displayName: 'Day', icon: 'mdi-calendar-today' },
  { value: 'TimelineWeek', displayName: 'Week', icon: 'mdi-calendar-week' },
  { value: 'TimelineWorkWeek', displayName: 'Work Week', icon: 'mdi-calendar-range' },
  { value: 'TimelineMonth', displayName: 'Month', icon: 'mdi-calendar-month' },
  { value: 'TimelineYear', displayName: 'Year', icon: 'mdi-calendar' },
  { value: 'Agenda', displayName: 'Agenda', icon: 'mdi-format-list-bulleted' }
]);
const activeTab = ref('timeline');
const taskDialogOpen = ref(false);
const selectedTask = ref({});
const workDays = ref([0, 1, 2, 3, 4, 5]);
const allowMultiple = ref(true);
const timelineScheduler = ref(null);

// Event settings with reactive dataSource
const dataSource = ref([]);
const filteredDataSource = computed(() => {
  if (selectedStatusFilter.value === 'All') {
    return dataSource.value;
  }
  return dataSource.value.filter(event => event.Status === selectedStatusFilter.value);
});

const eventSettings = computed(() => ({
  dataSource: filteredDataSource.value
}));

// Timeline event settings with hierarchy
const timelineDataSource = ref([]);
const filteredTimelineDataSource = computed(() => {
  if (selectedStatusFilter.value === 'All') {
    return timelineDataSource.value;
  }
  return timelineDataSource.value.filter(event => event.Status === selectedStatusFilter.value);
});

const timelineEventSettings = computed(() => ({
  dataSource: filteredTimelineDataSource.value
}));

// Resource data sources
const taskResourceDataSource = ref([]);

// Group configuration for timeline
const group = ref({
  resources: ['Tasks']
});

// Task status summary
const taskStatuses = ref([
  { name: 'Overdue', color: '#F44336', count: 0, icon: 'mdi-alert-circle' },
  { name: 'Un-Assigned', color: '#FF9800', count: 0, icon: 'mdi-account-question' },
  { name: 'Scheduled', color: '#4CAF50', count: 0, icon: 'mdi-calendar-clock' },
  { name: 'In Progress', color: '#2196F3', count: 0, icon: 'mdi-progress-clock' },
  { name: 'Complete', color: '#9E9E9E', count: 0, icon: 'mdi-check-circle' }
]);

// Status filter
const selectedStatusFilter = ref('All');
const statusFilterOptions = ref([
  { title: 'All', value: 'All', color: '#757575', icon: 'mdi-view-list' },
  { title: 'Overdue', value: 'Overdue', color: '#F44336', icon: 'mdi-alert-circle' },
  { title: 'In Progress', value: 'In Progress', color: '#2196F3', icon: 'mdi-progress-clock' },
  { title: 'Un-Assigned', value: 'Un-Assigned', color: '#FF9800', icon: 'mdi-account-question' },
  { title: 'Scheduled', value: 'Scheduled', color: '#4CAF50', icon: 'mdi-calendar-clock' },
  { title: 'Closed', value: 'Complete', color: '#9E9E9E', icon: 'mdi-check-circle' }
]);

// Helper function to get status count
const getStatusCount = (statusValue) => {
  console.log('Debug - getStatusCount called with:', statusValue);
  console.log('Debug - Current taskStatuses:', taskStatuses.value);
  const status = taskStatuses.value.find(s => 
    s.name === statusValue || (statusValue === 'Complete' && s.name === 'Complete')
  );
  console.log('Debug - Found status:', status);
  return status ? status.count : 0;
};

// Computed property for total task count
const totalTaskCount = computed(() => {
  return taskStatuses.value.reduce((sum, status) => sum + status.count, 0);
});

// Get the default schedule ID
const getDefaultScheduleId = async () => {
  const scheduleResult = await conn.query(`
    SELECT Id from schedules WHERE IsDefault = True
  `);
  
  const scheduleRows = scheduleResult.toArray();
  if (scheduleRows.length === 0) {
    throw new Error('No default schedule found');
  }
  
  return scheduleRows[0].Id;
};

// Download and store tasks data
const downloadTasksData = async (scheduleId) => {
  const formattedPath = `/api/${settings.value.projectCode}/tasks?nested=false&schedule-ids=${scheduleId}`;
  
  // Get JSON payload using ApiClient
  const apiClient = store.getters.getApiClient;
  const tasksData = await apiClient.get(formattedPath);
  
  // Process the JSON to extract subtasks
  const tasks = [];
  const subtasks = [];
  
  tasksData.forEach(task => {
    // Add the main task
    tasks.push(task);
    
    // Extract subtasks if they exist
    if (task.subtasks && Array.isArray(task.subtasks)) {
      task.subtasks.forEach(subTask => {
        // Add taskId property to each subtask
        subtasks.push({
          ...subTask,
          taskId: task.id
        });
      });
    }
  });
  
  // Drop existing tables
  await conn.query(`DROP TABLE IF EXISTS tasks`);
  await conn.query(`DROP TABLE IF EXISTS subtasks`);
  
  // Insert data into DuckDB using the service method
  if (tasks.length > 0) {
    await duckDBService.insertJSONToDuckDB(tasks, 'tasks');
  }
  if (subtasks.length > 0) {
    await duckDBService.insertJSONToDuckDB(subtasks, 'subtasks');
  } else {
    await conn.loadData(`
      CREATE TABLE subtasks(progress BIGINT,
        scheduleId VARCHAR,
        tags VARCHAR,
        id VARCHAR,
        title VARCHAR,
        leadByTeamId VARCHAR,
        weighting BIGINT,
        itemId VARCHAR,
        itemType VARCHAR,
        itemTemplateCode VARCHAR,
        meta VARCHAR,
        taskId VARCHAR);
      `)
  }

};

// Query tasks from the database (only leaf tasks) with subtask status info
const queryTasks = async (scheduleId) => {
  const tasksResult = await conn.query(`
    WITH unnested_tasks AS (
      SELECT 
        t.id,
        t.title,
        t.code,
        t.wbs,
        t.parentTaskId,
        t.childTaskIds,
        unnest(t.timeRanges) as timeRange
      FROM tasks t
      WHERE t.scheduleId = '${scheduleId}'
    ),
    task_subtask_status AS (
      SELECT 
        ut.id,
        ut.title,
        ut.code,
        ut.wbs,
        ut.parentTaskId,
        ut.childTaskIds,
        ut.timeRange,
        COUNT(DISTINCT s.id) as total_subtasks,
        COUNT(DISTINCT CASE WHEN s.itemId IS NOT NULL AND s.itemId != '' THEN s.id END) as subtasks_with_items,
        COUNT(DISTINCT CASE WHEN s.leadByTeamId IS NOT NULL AND s.leadByTeamId != '' THEN s.id END) as subtasks_with_lead_team,
        MAX(CASE WHEN s.leadByTeamId IS NOT NULL AND s.leadByTeamId != '' THEN 1 ELSE 0 END) as has_lead_team
      FROM unnested_tasks ut
      LEFT JOIN subtasks s ON ut.id = s.taskId
      GROUP BY ut.id, ut.title, ut.code, ut.wbs, ut.parentTaskId, ut.childTaskIds, ut.timeRange
    )
    SELECT * FROM task_subtask_status
    WHERE id NOT IN (
        SELECT DISTINCT parentTaskId 
        FROM tasks 
        WHERE parentTaskId IS NOT NULL 
          AND parentTaskId != ''
          AND scheduleId = '${scheduleId}'
      )
      AND timeRange.startDate IS NOT NULL
      AND timeRange.endDate IS NOT NULL
  `);
  
  const rows = tasksResult.toArray();
  return rows.map(row => {
    const jsonRow = row.toJSON();
    // Convert DuckDB timeRange object to plain JavaScript object
    if (jsonRow.timeRange && typeof jsonRow.timeRange === 'object') {
      try {
        // Extract the actual data from DuckDB object structure
        if (jsonRow.timeRange.toJSON) {
          jsonRow.timeRange = jsonRow.timeRange.toJSON();
        } else if (jsonRow.timeRange.data && Array.isArray(jsonRow.timeRange.data)) {
          // Handle DuckDB array data structure
          jsonRow.timeRange = jsonRow.timeRange.data[0];
        }
      } catch (e) {
        console.warn('Failed to convert timeRange object:', jsonRow.timeRange);
      }
    }
    return jsonRow;
  });
};


// Determine task status based on subtasks and dates
const determineTaskStatus = (task) => {
  const now = new Date();
  const startDate = new Date(task.timeRange.startDate);
  const endDate = new Date(task.timeRange.endDate);
  const progress = task.timeRange.progress || 0;
  
  // Complete: Task is 100%
  if (progress === 100) {
    return {
      status: 'Complete',
      color: '#9E9E9E', // Green
      description: 'Task is 100% complete'
    };
  }
  
  // In Progress: At least one subtask has an ItemId value
  if (task.subtasks_with_items > 0) {
    return {
      status: 'In Progress',
      color: '#2196F3', // Blue
      description: `${task.subtasks_with_items} of ${task.total_subtasks} subtasks have items`
    };
  }

    // Overdue: Task's subtasks have no ItemId value and past start date
  if (task.total_subtasks > 0 && task.subtasks_with_items === 0 && now > endDate) {
    return {
      status: 'Overdue',
      color: '#F44336', // Red
      description: 'Task is past due with no items assigned'
    };
  }
  
  // Past due without subtasks
  if (now > endDate) {
    return {
      status: 'Overdue',
      color: '#F44336', // Red
      description: 'Task is past its end date'
    };
  }
  
  // Scheduled: Task is created but not yet due to start
  if (now < startDate) {
    return {
      status: 'Scheduled',
      color: '#4CAF50', // Gray
      description: 'Task is scheduled to start'
    };
  }
  
  // Un-Assigned: Task is not overdue but has no LeadTeamId
  if (task.has_lead_team === 0) {
    return {
      status: 'Un-Assigned',
      color: '#FF9800', // Orange
      description: 'Task has no lead team assigned'
    };
  }
  
  // Default to In Progress if within date range
  return {
    status: 'In Progress',
    color: '#2196F3', // Blue
    description: 'Task is within scheduled dates'
  };
};

// Calculate task duration and format
const calculateTaskDuration = (startDate, endDate) => {
  const startTime = new Date(startDate);
  let endTime = new Date(endDate);
  
  // Calculate duration in milliseconds
  let durationMs = endTime.getTime() - startTime.getTime();
  
  // If duration is less than 1 hour (3600000 ms), set end time to be at least 1 hour after start
  const oneHourMs = 60 * 60 * 1000;
  if (durationMs < oneHourMs) {
    endTime = new Date(startTime.getTime() + oneHourMs);
  }
  
  // Recalculate duration after adjustment
  durationMs = endTime.getTime() - startTime.getTime();
  
  // Calculate duration in hours and days
  const durationInHours = Math.round(durationMs / (1000 * 60 * 60));
  const durationInDays = Math.round(durationMs / (1000 * 60 * 60 * 24) * 10) / 10; // Round to 1 decimal
  
  // Format duration string
  let durationString;
  if (durationInHours < 24) {
    durationString = `${durationInHours} hour${durationInHours !== 1 ? 's' : ''}`;
  } else {
    durationString = `${durationInDays} day${durationInDays !== 1 ? 's' : ''}`;
  }
  
  return {
    startTime,
    endTime,
    durationInHours,
    durationString
  };
};

// Transform task to scheduler event format
const transformTaskToEvent = (task, index) => {
  const { startTime, endTime, durationInHours, durationString } = calculateTaskDuration(
    task.timeRange.startDate,
    task.timeRange.endDate
  );
  
  const taskStatus = determineTaskStatus(task);
  
  return {
    Id: index + 1,
    Subject: `${task.title || task.code || 'Untitled Task'}`,
    StartTime: startTime,
    EndTime: endTime,
    IsAllDay: durationInHours >= 24, // Only all-day if duration is 24 hours or more
    Description: `Status: ${taskStatus.status} - ${taskStatus.description}\nCode: ${task.code || 'N/A'}\nWBS: ${Array.isArray(task.wbs) ? task.wbs.join('.') : 'N/A'}\nProgress: ${task.timeRange.progress || 0}%\nType: ${task.timeRange.timeRangeTemplateType || 'N/A'}\nDuration: ${durationString}\nSubtasks: ${task.total_subtasks || 0} (${task.subtasks_with_items || 0} with items)`,
    TaskId: task.id,
    ActualTaskId: task.id, // Same as TaskId for calendar view
    Progress: task.timeRange.progress || 0,
    TimeRangeType: task.timeRange.timeRangeTemplateType,
    TimeRangeTitle: task.timeRange.timeRangeTemplateTitle,
    DurationInHours: durationInHours,
    CategoryColor: taskStatus.color,
    Status: taskStatus.status,
    StatusColor: taskStatus.color,
    StatusDescription: taskStatus.description,
    TotalSubtasks: task.total_subtasks || 0,
    SubtasksWithItems: task.subtasks_with_items || 0
  };
};

// Build task hierarchy from ParentId relationships
const buildTaskHierarchy = (tasks) => {
  const taskMap = new Map();
  const rootTasks = [];
  
  // First pass: create task map
  tasks.forEach(task => {
    taskMap.set(task.id, { ...task, children: [] });
  });
  
  // Second pass: build hierarchy
  tasks.forEach(task => {
    const taskWithChildren = taskMap.get(task.id);
    if (task.parentTaskId && taskMap.has(task.parentTaskId)) {
      // Add to parent's children
      taskMap.get(task.parentTaskId).children.push(taskWithChildren);
    } else {
      // This is a root task
      rootTasks.push(taskWithChildren);
    }
  });
  
  return { taskMap, rootTasks };
};

// Query all tasks with hierarchy
const queryAllTasks = async (scheduleId) => {
  const tasksResult = await conn.query(`
    SELECT 
      id,
      code,
      title,
      parentTaskId,
      childTaskIds,
      timeRanges,
      scheduleId,
      sort,
      wbs
    FROM tasks 
    WHERE scheduleId = '${scheduleId}'
    ORDER BY sort ASC
  `);
  
  const rows = tasksResult.toArray();
  return rows.map(row => {
    const jsonRow = row.toJSON();
    // Convert DuckDB timeRanges array to plain JavaScript array
    if (jsonRow.timeRanges && typeof jsonRow.timeRanges === 'object') {
      try {
        // Extract the actual data from DuckDB array structure
        if (jsonRow.timeRanges.toJSON) {
          jsonRow.timeRanges = jsonRow.timeRanges.toJSON();
        } else if (jsonRow.timeRanges.data && Array.isArray(jsonRow.timeRanges.data)) {
          // Handle DuckDB array data structure
          jsonRow.timeRanges = jsonRow.timeRanges.data;
        } else if (Array.isArray(jsonRow.timeRanges)) {
          // Already a proper array, no conversion needed
        }
      } catch (e) {
        console.warn('Failed to convert timeRanges array:', jsonRow.timeRanges);
      }
    }
    return jsonRow;
  });
};

// Generate project and task resources for timeline
const generateTimelineResources = async (scheduleId) => {
  try {
    // Get all tasks including parent tasks
    const allTasks = await queryAllTasks(scheduleId);
    
    // Find all parent tasks (tasks that have children)
    const parentTasks = [];
    const leafTasks = [];
    
    allTasks.forEach(task => {
      const hasChildren = allTasks.some(t => t.parentTaskId === task.id);
      if (hasChildren) {
        parentTasks.push(task);
      } else {
        leafTasks.push(task);
      }
    });
    
    // Generate parent task resources only
    const taskResources = parentTasks.map((task, index) => ({
      text: task.title || task.code || 'Untitled Parent',
      id: task.id,
      color: getTaskColor(index)
    }));
    
    return { taskResources, allTasks, leafTasks, parentTasks };
  } catch (error) {
    console.error('Error generating timeline resources:', error);
    return { taskResources: [], allTasks: [], leafTasks: [], parentTasks: [] };
  }
};

// Color generation functions
const getTaskColor = (index) => {
  const colors = ['#df5286', '#7fa900', '#ea7a57', '#5978ee', '#00bdae', '#ff6b35'];
  return colors[index % colors.length];
};

// Transform leaf task to timeline event (assigned to parent task resource)
const transformLeafTaskToTimelineEvent = (task, index, parentTaskId) => {
  // Only create events for tasks with time ranges
  if (!task.timeRanges || task.timeRanges.length === 0) {
    return null;
  }
  
  const timeRange = task.timeRanges[0]; // Use first time range
  if (!timeRange.startDate || !timeRange.endDate) {
    return null;
  }
  
  const { startTime, endTime, durationInHours, durationString } = calculateTaskDuration(
    timeRange.startDate,
    timeRange.endDate
  );
  
  // Create a temporary task object for status determination
  const tempTask = {
    timeRange: timeRange,
    total_subtasks: 0,
    subtasks_with_items: 0,
    has_lead_team: 1 // Assume assigned for timeline view
  };
  
  const taskStatus = determineTaskStatus(tempTask);
  
  return {
    Id: index + 1,
    Subject: task.title || task.code || 'Untitled Task',
    StartTime: startTime,
    EndTime: endTime,
    IsAllDay: durationInHours >= 24,
    Description: `Status: ${taskStatus.status} - ${taskStatus.description}\nCode: ${task.code || 'N/A'}\nWBS: ${Array.isArray(task.wbs) ? task.wbs.join('.') : 'N/A'}\nProgress: ${timeRange.progress || 0}%\nDuration: ${durationString}`,
    TaskId: parentTaskId, // Used for resource grouping in timeline view
    ActualTaskId: task.id, // The actual task ID for fetching subtasks
    Progress: timeRange.progress || 0,
    CategoryColor: taskStatus.color,
    Status: taskStatus.status,
    StatusColor: taskStatus.color,
    StatusDescription: taskStatus.description
  };
};

// Main initialization function
const initializeData = async () => {
  try {
    const scheduleId = await getDefaultScheduleId();
    await downloadTasksData(scheduleId);
    
    // Initialize calendar view data
    const tasks = await queryTasks(scheduleId);
    const events = tasks.map(transformTaskToEvent);
    dataSource.value = events;
    console.log('Debug - Events loaded:', events.length, 'events');
    console.log('Debug - First few events:', events.slice(0, 3));
    
    // Initialize timeline view data
    const { taskResources, leafTasks } = await generateTimelineResources(scheduleId);
    taskResourceDataSource.value = taskResources;
    
    // Generate timeline events from leaf tasks only
    const timelineEvents = leafTasks
      .map((task, index) => transformLeafTaskToTimelineEvent(task, index, task.parentTaskId))
      .filter(event => event !== null);
    timelineDataSource.value = timelineEvents;
    
    // Count task statuses
    const statusCounts = {
      'Overdue': 0,
      'Un-Assigned': 0,
      'Scheduled': 0,
      'In Progress': 0,
      'Complete': 0
    };
    
    events.forEach(event => {
      if (event.Status in statusCounts) {
        statusCounts[event.Status]++;
      }
    });
    
    // Update the taskStatuses ref with counts
    taskStatuses.value = taskStatuses.value.map(status => ({
      ...status,
      count: statusCounts[status.name] || 0
    }));
    console.log('Debug - Task statuses after update:', taskStatuses.value);
    console.log('Debug - Status counts:', statusCounts);
    
    console.log(`Loaded ${events.length} calendar tasks and ${timelineEvents.length} timeline tasks from schedule ${scheduleId}`);
    console.log('Timeline Task Resources:', taskResources.slice(0, 10)); // Log first 10
    
  } catch (error) {
    console.error('Error loading tasks:', error);
  }
};

// Handle event click
const handleEventClick = (args) => {
  // Prevent the default quick info popup
  args.cancel = true;
  
  // Set the selected task and open the dialog
  selectedTask.value = args.event;
  taskDialogOpen.value = true;
};

// Handle event rendering to apply custom colors
const onEventRendered = (args) => {
  // Apply the status color to the event element
  if (args.data.StatusColor || args.data.CategoryColor) {
    const color = args.data.StatusColor || args.data.CategoryColor;
    
    // Apply background and border colors with !important to override default styles
    args.element.style.setProperty('background-color', color, 'important');
    args.element.style.setProperty('border-color', color, 'important');
    args.element.style.setProperty('border-left-color', color, 'important');
    
    // Ensure text remains visible
    args.element.style.setProperty('color', '#FFFFFF', 'important');
    
    // For all-day events and month view events
    if (args.element.classList.contains('e-all-day-cells') || 
        args.element.classList.contains('e-appointment')) {
      args.element.style.setProperty('background-color', color, 'important');
    }
    
    // Apply to any child elements that might contain the content
    const subjectElement = args.element.querySelector('.e-subject');
    if (subjectElement) {
      subjectElement.style.setProperty('color', '#FFFFFF', 'important');
    }
    
    // Apply to appointment wrapper if exists
    const appointmentWrapper = args.element.querySelector('.e-appointment-wrapper');
    if (appointmentWrapper) {
      appointmentWrapper.style.setProperty('background-color', color, 'important');
    }
  }
};

// Date navigation functions
const navigateDate = (direction) => {
  const currentDate = selectedDate.value;
  let newDate;
  
  switch (timelineView.value) {
    case 'TimelineDay':
      newDate = direction === 'next' ? addDays(currentDate, 1) : addDays(currentDate, -1);
      break;
    case 'TimelineWeek':
    case 'TimelineWorkWeek':
      newDate = direction === 'next' ? addWeeks(currentDate, 1) : addWeeks(currentDate, -1);
      break;
    case 'TimelineMonth':
      newDate = direction === 'next' ? addMonths(currentDate, 1) : addMonths(currentDate, -1);
      break;
    case 'TimelineYear':
      newDate = direction === 'next' ? addYears(currentDate, 1) : addYears(currentDate, -1);
      break;
    case 'Agenda':
      newDate = direction === 'next' ? addDays(currentDate, 7) : addDays(currentDate, -7);
      break;
    default:
      newDate = currentDate;
  }
  
  selectedDate.value = newDate;
};

const navigateToToday = () => {
  selectedDate.value = new Date();
};

const getCurrentDateRange = () => {
  const date = selectedDate.value;
  
  switch (timelineView.value) {
    case 'TimelineDay':
      return format(date, 'EEEE, MMMM d, yyyy');
    case 'TimelineWeek':
    case 'TimelineWorkWeek': {
      const weekStart = startOfWeek(date);
      const weekEnd = endOfWeek(date);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    case 'TimelineMonth':
      return format(date, 'MMMM yyyy');
    case 'TimelineYear':
      return format(date, 'yyyy');
    case 'Agenda':
      return format(date, 'MMMM d, yyyy');
    default:
      return '';
  }
};

// Watch for timeline view changes
// Dynamic CSS loading for dark theme
const loadDarkThemeCSS = () => {
  const darkThemeFiles = [
    '@syncfusion/ej2-base/styles/material-dark.css',
    '@syncfusion/ej2-buttons/styles/material-dark.css',
    '@syncfusion/ej2-calendars/styles/material-dark.css',
    '@syncfusion/ej2-dropdowns/styles/material-dark.css',
    '@syncfusion/ej2-inputs/styles/material-dark.css',
    '@syncfusion/ej2-navigations/styles/material-dark.css',
    '@syncfusion/ej2-popups/styles/material-dark.css',
    '@syncfusion/ej2-vue-schedule/styles/material-dark.css'
  ];

  darkThemeFiles.forEach(file => {
    const linkId = `syncfusion-dark-${file.split('/')[1]}`;
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `/node_modules/${file}`;
      document.head.appendChild(link);
    }
  });
};

const removeDarkThemeCSS = () => {
  const darkThemeFiles = [
    'syncfusion-dark-ej2-base',
    'syncfusion-dark-ej2-buttons',
    'syncfusion-dark-ej2-calendars',
    'syncfusion-dark-ej2-dropdowns',
    'syncfusion-dark-ej2-inputs',
    'syncfusion-dark-ej2-navigations',
    'syncfusion-dark-ej2-popups',
    'syncfusion-dark-ej2-vue-schedule'
  ];

  darkThemeFiles.forEach(id => {
    const link = document.getElementById(id);
    if (link) {
      link.remove();
    }
  });
};

// Watch theme changes to load/unload dark theme CSS
watch(themeMode, (isDark) => {
  if (isDark) {
    loadDarkThemeCSS();
  } else {
    removeDarkThemeCSS();
  }
}, { immediate: true });

watch(timelineView, async (newView, oldView) => {
  console.log('Timeline view changing from', oldView, 'to', newView);
  await nextTick();
  
  if (timelineScheduler.value && timelineScheduler.value.ej2Instances) {
    const scheduler = timelineScheduler.value.ej2Instances;
    console.log('Scheduler instance found, current view:', scheduler.currentView);
    console.log('Available methods:', Object.keys(scheduler).filter(key => typeof scheduler[key] === 'function').slice(0, 10));
    
    // Try different methods to change the view
    try {
      // Method 1: changeCurrentView (from earlier attempts)
      if (scheduler.changeCurrentView) {
        console.log('Using changeCurrentView method');
        scheduler.changeCurrentView(newView);
      } 
      // Method 2: changeView
      else if (scheduler.changeView) {
        console.log('Using changeView method');
        scheduler.changeView(newView);
      } 
      // Method 3: Direct property set with refresh
      else {
        console.log('Using direct property set');
        scheduler.currentView = newView;
        scheduler.dataBind();
        scheduler.refresh();
      }
      
      console.log('View after change:', scheduler.currentView);
    } catch (error) {
      console.error('Error changing view:', error);
    }
  } else {
    console.log('Scheduler not found or not initialized');
  }
});

onMounted(() => {
  initializeData();
});


</script>

<style>
@import "@syncfusion/ej2-base/styles/material.css";
@import "@syncfusion/ej2-buttons/styles/material.css";
@import "@syncfusion/ej2-calendars/styles/material.css";
@import "@syncfusion/ej2-dropdowns/styles/material.css";
@import "@syncfusion/ej2-inputs/styles/material.css";
@import "@syncfusion/ej2-navigations/styles/material.css";
@import "@syncfusion/ej2-popups/styles/material.css";
@import "@syncfusion/ej2-vue-schedule/styles/material.css";

/* Dark theme imports */


/* Dark theme custom overrides to match RDrive theme */

.e-dark.e-schedule {
  background-color: #263e4e; /* surface */
  border-color: #1a292f; /* primary-darken-1 */
}

.e-dark.e-schedule .e-header-cells,
.e-dark.e-schedule .e-all-day-cells,
.e-dark.e-schedule .e-work-cells {
  background-color: #213542; /* background */
  border-color: #1a292f; /* primary-darken-1 */
}

.e-dark.e-schedule .e-header-cells {
  background-color: #1a292f; /* primary-darken-1 */
  color: #fff; /* primary */
  font-weight: 500;
}

.e-dark.e-schedule .e-time-cells-wrap,
.e-dark.e-schedule .e-date-header-wrap {
  background-color: #263e4e; /* surface */
}

.e-dark.e-schedule .e-schedule-toolbar {
  background-color: #213542; /* background */
  border-color: #1a292f; /* primary-darken-1 */
}

.e-dark.e-schedule .e-toolbar .e-btn {
  background-color: transparent;
  color: #fff; /* primary */
}

.e-dark.e-schedule .e-toolbar .e-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.e-dark.e-schedule .e-toolbar .e-btn.e-active {
  background-color: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.e-dark.e-schedule .e-resource-cells {
  background-color: #213542; /* background */
  color: #fff; /* primary */
  border-color: #1a292f; /* primary-darken-1 */
}

.e-dark.e-schedule .e-resource-text {
  color: #fff !important; /* primary */
}

.e-dark.e-schedule .e-resource-name {
  color: #fff !important; /* primary */
}

.e-dark.e-schedule .e-vertical-view .e-time-cells,
.e-dark.e-schedule .e-month-view .e-work-cells,
.e-dark.e-schedule .e-month-view .e-other-month {
  background-color: #213542; /* background */
  border-color: #1a292f; /* primary-darken-1 */
  color: rgba(255, 255, 255, 0.7);
}

.e-dark.e-schedule .e-current-day {
  background-color: rgba(33, 150, 243, 0.12) !important; /* info with transparency */
}

.e-dark.e-schedule .e-selected-cells {
  background-color: rgba(33, 150, 243, 0.24) !important; /* info with transparency */
}

/* Additional dark theme styles for better integration */
.e-dark.e-schedule .e-date-header-container .e-header-cells {
  color: #fff;
}

.e-dark.e-schedule .e-timeline-view .e-date-header-wrap table td,
.e-dark.e-schedule .e-timeline-month-view .e-date-header-wrap table td {
  color: #fff;
  background-color: #1a292f !important; /* primary-darken-1 */
}

/* Timeline header cells - all levels */
.e-dark.e-schedule .e-timeline-view .e-header-cells,
.e-dark.e-schedule .e-timeline-month-view .e-header-cells {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Timeline date header specific */
.e-dark.e-schedule .e-timeline-view .e-date-header-wrap,
.e-dark.e-schedule .e-timeline-month-view .e-date-header-wrap {
  background-color: #1a292f !important; /* primary-darken-1 */
}

/* Timeline header row cells */
.e-dark.e-schedule .e-timeline-view .e-header-row td,
.e-dark.e-schedule .e-timeline-month-view .e-header-row td {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Timeline top-left corner cell */
.e-dark.e-schedule .e-timeline-view .e-resource-left-td.e-header-cells,
.e-dark.e-schedule .e-timeline-month-view .e-resource-left-td.e-header-cells,
.e-dark.e-schedule .e-timeline-view .e-left-indent,
.e-dark.e-schedule .e-timeline-month-view .e-left-indent {
  background-color: #1a292f !important; /* primary-darken-1 */
  border-color: #213542 !important; /* background */
}

.e-dark.e-schedule .e-time-slots {
  color: rgba(255, 255, 255, 0.7);
}

.e-dark.e-schedule .e-timeline-view .e-resource-left-td {
  background-color: #213542; /* background */
  border-color: #1a292f; /* primary-darken-1 */
}

.e-dark.e-schedule .e-timeline-view .e-resource-cells {
  background-color: #1a292f; /* primary-darken-1 */
}

/* Hover states */
.e-dark.e-schedule .e-work-cells:hover,
.e-dark.e-schedule .e-header-cells:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

/* Navigation buttons in toolbar */
.e-dark.e-schedule .e-toolbar .e-tbar-btn {
  color: #fff;
}

.e-dark.e-schedule .e-toolbar .e-tbar-btn:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

/* Date navigation text */
.e-dark.e-schedule .e-date-range {
  color: #fff;
}

/* View switcher buttons */
.e-dark.e-schedule .e-toolbar .e-views .e-btn {
  color: #fff;
}

.e-dark.e-schedule .e-toolbar .e-views .e-btn.e-active {
  background-color: #2196F3; /* info */
  color: #fff;
}

.e-dark.e-schedule .e-agenda-view .e-day-date-header,
.e-dark.e-schedule .e-agenda-view .e-day-time-header {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
}

/* Agenda view date cells */
.e-dark.e-schedule .e-agenda-view .e-date-header,
.e-dark.e-schedule .e-agenda-view .e-date-header td,
.e-dark.e-schedule .e-agenda-view .e-agenda-date-column {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Agenda view time cells */
.e-dark.e-schedule .e-agenda-view .e-time-header,
.e-dark.e-schedule .e-agenda-view .e-time-header td,
.e-dark.e-schedule .e-agenda-view .e-agenda-time-column {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Agenda view header rows */
.e-dark.e-schedule .e-agenda-view .e-header-row,
.e-dark.e-schedule .e-agenda-view .e-header-row td {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
}

/* Agenda view resource column */
.e-dark.e-schedule .e-agenda-view .e-resource-column {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Agenda view date column cells */
.e-dark.e-schedule .e-agenda-view .e-agenda-cells.e-date-column {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Agenda view resource cells */
.e-dark.e-schedule .e-agenda-view .e-resource-cells {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Year view table cells */
.e-dark.e-schedule .e-schedule-table.e-content-table,
.e-dark.e-schedule .e-schedule-table.e-content-table td,
.e-dark.e-schedule .e-year-view .e-content-table,
.e-dark.e-schedule .e-year-view .e-content-table td {
  background-color: #213542 !important; /* background */
  color: #fff !important;
  border-color: #1a292f !important; /* primary-darken-1 */
}

/* Date header container */
.e-dark.e-schedule .e-date-header-container {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Additional date header elements */
.e-dark.e-schedule .e-date-header-wrap {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
}

/* Schedule table general */
.e-dark.e-schedule .e-schedule-table {
  background-color: #213542 !important; /* background */
  color: #fff !important;
  border-color: #1a292f !important; /* primary-darken-1 */
}

/* Resource cells general */
.e-dark.e-schedule .e-resource-cells {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* Resource left table cell */
.e-dark.e-schedule .e-resource-left-td {
  background-color: #1a292f !important; /* primary-darken-1 */
  color: #fff !important;
  border-color: #213542 !important; /* background */
}

/* More/less appointment count */
.e-dark.e-schedule .e-more-appointment-indicator,
.e-dark.e-schedule .e-more-indicator {
  color: #2196F3; /* info */
  background-color: transparent;
}

/* Quick popup */
.e-dark.e-schedule-dialog .e-dlg-header,
.e-dark.e-quick-popup-wrapper .e-popup-header {
  background-color: #1a292f; /* primary-darken-1 */
  color: #fff;
}

.e-dark.e-schedule-dialog .e-dlg-content,
.e-dark.e-quick-popup-wrapper .e-popup-content {
  background-color: #263e4e; /* surface */
  color: #fff;
}

/* Dropdown and input fields in popups */
.e-dark.e-schedule-dialog .e-input-group input,
.e-dark.e-schedule-dialog .e-dropdown,
.e-dark.e-schedule-dialog .e-multiselect {
  background-color: #213542; /* background */
  color: #fff;
  border-color: #1a292f; /* primary-darken-1 */
}

/* Scrollbar styling */
.e-dark.e-schedule ::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.e-dark.e-schedule ::-webkit-scrollbar-track {
  background: #213542; /* background */
}

.e-dark.e-schedule ::-webkit-scrollbar-thumb {
  background: #1a292f; /* primary-darken-1 */
  border-radius: 4px;
}

.e-dark.e-schedule ::-webkit-scrollbar-thumb:hover {
  background: #424242; /* secondary */
}

/* Custom styles for event colors */
.e-schedule .e-appointment {
  border-width: 2px !important;
  border-radius: 4px !important;
}

.e-schedule .e-appointment .e-subject {
  font-weight: 500 !important;
  color: #FFFFFF !important;
}

.e-schedule .e-appointment .e-appointment-details {
  color: #FFFFFF !important;
}

/* Ensure proper contrast for all views */
.e-schedule .e-month-view .e-appointment,
.e-schedule .e-week-view .e-appointment,
.e-schedule .e-day-view .e-appointment,
.e-schedule .e-agenda-view .e-appointment,
.e-schedule .e-timeline-view .e-appointment {
  color: #FFFFFF !important;
}

/* Style for all-day appointments */
.e-schedule .e-all-day-appointment-wrapper .e-appointment {
  border-left-width: 4px !important;
}

/* Timeline view specific styles */
.e-schedule .e-timeline-view .e-appointment {
  border-left-width: 4px !important;
  color: #FFFFFF !important;
}

.e-schedule .e-timeline-view .e-appointment .e-subject {
  color: #FFFFFF !important;
  font-weight: 500 !important;
}

/* Month view event styling */
.e-schedule .e-month-view .e-appointment {
  border-radius: 3px !important;
  border-left-width: 4px !important;
}

/* Week and day view styling */
.e-schedule .e-week-view .e-appointment,
.e-schedule .e-day-view .e-appointment {
  border-radius: 4px !important;
  border-left-width: 4px !important;
}

/* Agenda view styling */
.e-schedule .e-agenda-view .e-appointment-indicator {
  border-radius: 50% !important;
  width: 12px !important;
  height: 12px !important;
}

/* Custom styles for status filter buttons */
.v-btn-toggle {
  gap: 8px;
}

.v-btn-toggle .v-btn {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}

.v-btn-toggle .v-btn .v-badge__badge {
  font-size: 11px;
  height: 18px;
  min-width: 18px;
  padding: 0 6px;
}
</style>